import React, { useState, useEffect } from 'react';
import Button from '../../ui/Button';
import { debateTypes } from '../debateFormatsDataV3';
import FAQItem from './FAQItem';
import DebateFormatGallery from '../DebateFormatGallery/DebateFormatGallery';
import DebateFormatCarousel from '../DebateFormatCarousel/DebateFormatCarousel';
import UserSelectField from '../UserSelectField/UserSelectField';
import styles from './Step2FormatAndRoles.module.css';
import UserSearchGeneric from '../../../utils/UserSearchGeneric/UserSearchGeneric';
import { useAuth } from "./../../../utils/AuthContext/authcontext";
import DebaterAssign from '../../DebaterAssign/DebaterAssign';



export default function Step2FormatAndRoles({ faqs, data, onChange, onBack, onFinish }) {
  const { formatId, custom, roles, schedule } = data;
  const [myRole, setMyRole] = useState('none');
  const [errors, setErrors] = useState({});
  const [searchTarget, setSearchTarget] = useState(null);
  const [searchTargetTeam, setSearchTargetTeam] = useState(null);
  const [cachedResults, setCachedResults] = useState({});
  const format = debateTypes.find(f => f.id === formatId);
  const totalTurnDurationSec = (format?.turns || []).reduce((acc, turn) => acc + (turn?.durationSec || 0), 0);
  const totalTurnDurationMin = Math.floor(totalTurnDurationSec / 60);
  const totalTurnDurationRemSec = totalTurnDurationSec % 60;
  const {user, userData: currentUser, searchUsers} = useAuth();
  
  // Initialize defaults and reset on format change
  useEffect(() => {
    if (formatId && !custom.initialized) {
      onChange(prev => ({
        custom: {
          rounds: format.turns[0]?.rounds || 1,
          time: totalTurnDurationSec || 0,
          participants: format.participants?.min || 1,
		      teams: format.teams,
		      teamsnumber: format.teamsnumber,
          visibility: 'public',
          wager: 0,
          initialized: true,
        },
        roles: { debaters: [], moderator: null },
        schedule: { date: '', time: '' },
      }));
      setMyRole('none');
	  setErrors({});
    }
  }, [formatId]);

  // Sync 'My Role'
  useEffect(() => {
    if (!currentUser) return;
    const me = { uid: currentUser.uid, username: currentUser.username, email: currentUser.email };

    onChange(prev => {
      let { debaters = [], moderator = null } = prev.roles;
      debaters = debaters.filter(d => d?.uid !== me.uid);
      if (moderator?.uid === me.uid) moderator = null;

	  if (myRole === 'participant') {
		const arr = [...(debaters || [])];
		const alreadyExists = arr.some(p => p?.uid === me.uid);
		if (!alreadyExists) debaters[0] = me;
	  }
	  
      //if (myRole === 'participant' && debaters.length < custom.participants)
      //  debaters.push(me);

      if (myRole === 'moderator' && format.rules.moderator)
        moderator = me;

      return { roles: { debaters, moderator } };
    });
	setErrors({});
  }, [myRole]);

  // Prevent duplicates on manual user selection
  const handleUserSelect = (type, user, i, teamName) => {
	  onChange(prev => {
		const { debaters = [], moderator } = prev.roles;

		// Extract all identifiers already used
		const usedUIDs = debaters.map(d => d?.uid).concat(moderator?.uid);
		const usedEmails = debaters.map(d => d?.email).concat(moderator?.email);

		const isDuplicate = user?.uid ? usedUIDs.includes(user.uid) : user?.email && usedEmails.includes(user?.email);

		if (isDuplicate) return prev; // skip if already selected

		const updated = { ...prev.roles };
		if(searchTarget != null) {
			if (searchTarget === 'mod') {
			  const you = { uid: user.uid, username: user.username, email: user.email, profilePicture: user.profilePicture};
			  updated.moderator = you;
			} else {
			  const newDebaters = [...debaters];
			  const you = { uid: user.uid, username: user.username, email: user.email, team: searchTargetTeam, profilePicture: user.profilePicture};
			  newDebaters[searchTarget] = you;
			  updated.debaters = newDebaters;
			}
		}else {
			if(type) {
				updated.moderator = user;
			} else {
			  const newDebaters = [...debaters];
			  if(user != null && teamName != undefined) {
				user['team'] = teamName;
				newDebaters[i] = user;
			  }
			  else {
				newDebaters[i] = user;
			  }
			  updated.debaters = newDebaters;
			}
		}
		return { ...prev, roles: updated };
	  });
	  setErrors({});
	  setSearchTarget(null);
	  setSearchTargetTeam(null);
	};

	const getBalancedTeamSlots = () => {
	  const totalSlots = custom.participants;
	  const teamCount = custom.teams.length;
	  const base = Math.floor(totalSlots / teamCount);
	  const remainder = totalSlots % teamCount;

	  return custom.teams.map((_, i) => base + (i < remainder ? 1 : 0));
	};

  // Form Validation
  const validate = () => {
    const errs = {};
    if (!formatId) errs.format = 'Select a format.';
    if (custom.participants < format.participants.min || custom.participants > (format.participants.max || Infinity)) {
      errs.participants = `Participants must be between ${format.participants.min}${format.participants.max ? ' and ' + format.participants.max : '+'}.`;
    }

    if (custom.rounds < (1)) {
      errs.rounds = `At least 1 rounds required.`;
    }
    /*if (custom.time < (format.rules?.time?.min || 1) || custom.time > (format.rules?.time?.max || Infinity)) {
      errs.time = `Time must be between ${format.rules?.time?.min || 1}-${format.rules?.time?.max || Infinity}s.`;
    }*/
    if (!schedule.date || !schedule.time)
      errs.schedule = 'Debate date and time are required.';
    const dt = new Date(`${schedule.date}T${schedule.time}`);
    if (isNaN(dt) || dt < new Date())
      errs.schedule = 'Schedule must be a valid future datetime.';
    if (!roles.debaters?.length || roles.debaters.filter(d => d).length < format.participants.min)
      errs.debaters = `Add at least ${format.participants.min} debaters.`;
    if (format?.roles?.moderator?.exists && !roles.moderator)
      errs.moderator = 'Moderator is required.';
    if (format?.roles?.judge?.exists && !roles.moderator)
      errs.moderator = 'Judge is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

const handleFinish = () => {
    if (!validate()) {
      //window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    onFinish();
  };
var isModeratorValid = format?.roles?.judge?.exists === true || format?.roles?.moderator?.exists === true || false;


const getRoleFaq = (format) => {
  const hasModerator = format.roles?.moderator?.exists;
  const hasJudge = format.roles?.judge?.exists;

  if (hasModerator && hasJudge) {
    return {
      question: 'What is the difference between a moderator and a judge?',
      answer: 'The moderator controls how the debate runs — they start the debate, manage speaking turns, and enforce timing and rules. The judge focuses on evaluation — they assess arguments, assign scores, and determine the winner. In some debates, one person may take on both roles, or debates may run without either role.'
    };
  }

  if (!hasModerator && hasJudge) {
    return {
      question: 'Who controls the debate if there is no moderator?',
      answer: 'The judge may also manage the flow of the debate, including starting turns and maintaining order.'
    };
  }

  if (!hasModerator && !hasJudge) {
    return {
      question: 'Who controls the debate?',
      answer: 'In this format, debaters manage the flow themselves, including starting and ending turns.'
    };
  }

  return null;
}

const deriveFaqs = (format) => {
  if (!format) return [];

  const faqs = [];

  // Basic info
  faqs.push({
    question: `What is ${format.title}?`,
    answer: format.description
  });

  // Roles
  //if(getRoleFaq(format) != null)
  //  faqs.push(getRoleFaq(format));

  // Turns
  const totalTurns = format.turns?.length || 0;
  if (totalTurns) {
    faqs.push({
      question: 'How many turns are there?',
      answer: `${totalTurns} turns in total`
    });
  }

  // Timing
  if (format.turns?.length) {
    const totalTime = format.turns.reduce((a, t) => a + (t.durationSec || 0), 0);
    const avg = Math.round(totalTime / format.turns.length);

    faqs.push({
      question: 'How long is each turn?',
      answer: `On average ${avg} seconds per turn`
    });

    faqs.push({
      question: 'What is the total debate duration?',
      answer: `${Math.round(totalTime / 60)} minutes total`
    });
  }

  // Teams
  if (format.teams) {
    faqs.push({
      question: 'How many teams are there?',
      answer: `${format.teams.length} teams`
    });
  }

  // Team size
  if (format.teamsnumber) {
    faqs.push({
      question: 'How many speakers are in each team?',
      answer: format.teamsnumber.join(' vs ')
    });
  }

  // Spectators
  if (format.spectators?.canParticipate) {
    faqs.push({
      question: 'Can spectators participate?',
      answer: `Yes, via: ${(format.spectators.features || []).join(', ')}`
    });
  }

  // POI
  if (format.turns?.some(t => t.type === 'poi')) {
    faqs.push({
      question: 'Are Points of Information (POIs) allowed?',
      answer: 'Yes, short interruptions/questions are allowed during specific turns.'
    });
  }

  // Cross-examination
  if (format.turns?.some(t => t.type === 'cross-examination')) {
    faqs.push({
      question: 'Is cross-examination included?',
      answer: 'Yes, participants directly question each other.'
    });
  }

  // Queue formats
  if (['fishbowl', 'townhall'].includes(format.id)) {
    faqs.push({
      question: 'How does the speaker queue work?',
      answer: 'Participants can request to speak and are admitted by the moderator.'
    });
  }

  // Rounds
  const rounds = new Set(format.turns?.map(t => t.round));
  if (rounds.size > 1) {
    faqs.push({
      question: 'Are there multiple rounds?',
      answer: `Yes, this debate has ${rounds.size} rounds`
    });
  }

  // Replies / summaries
  if (format.turns?.some(t => t.type === 'reply')) {
    faqs.push({
      question: 'Are there closing speeches?',
      answer: 'Yes, reply speeches summarize and conclude each team’s arguments.'
    });
  }

  return faqs;
}
//console.log(getRoleFaq(format),deriveFaqs(format),format.faqs);

const formatFaqs = format
  ? [
      (getRoleFaq(format) || []),
      ...(format.faqs || []),
      ...deriveFaqs(format)
    ]
  : [];

const roleLabel = (() => {
  if (format?.roles?.moderator?.exists === true) return 'Moderator';
  if (format?.roles?.judge?.exists === true) return 'Judge';
  return null; // fallback (or hide entirely if you prefer)
})();

return (
  <div className={styles.step}>

    {/* FORMAT */}
    <section className={styles.card}>
      <div className={styles.cardHeader}>
        <h2>Select Format</h2>
        <p>Choose the debate structure that suits your topic.</p>
      </div>

      {errors?.format && <p className={styles.error}>{errors.format}</p>}

      <DebateFormatCarousel
        onSelect={id =>
          onChange(prev => ({
            formatId: id,
            custom: { ...prev.custom, initialized: false },
          }))
        }
      />

      <div className={styles.faqSection}>{faqs(formatFaqs)}</div>
    </section>

    {format && (
      <>
        {/* CUSTOMIZE */}
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Customize Format</h2>
            <p>Adjust participants, rounds, visibility, and wager.</p>
          </div>

          <div className={styles.grid}>
            {/* ROUNDS */}
            <div className={`${styles.field} ${styles.span4}`}>
              <label>Rounds</label>
              <input
                type="number"
                min={1}
                max={10}
                value={custom.rounds || '1'}
                onChange={e =>
                  onChange(prev => ({
                    custom: { ...prev.custom, rounds: +e.target.value },
                  }))
                }
              />
            </div>

            {/* PARTICIPANTS */}
            <div className={`${styles.field} ${styles.span4}`}>
              <label>Participants</label>
              <input
                type="number"
                min={format.participants.min}
                max={format.participants?.max || ''}
                value={custom.participants || ''}
                onChange={e =>
                  onChange(prev => ({
                    custom: { ...prev.custom, participants: +e.target.value },
                  }))
                }
              />
              {errors.participants && <p className={styles.error}>{errors.participants}</p>}
            </div>

            {/* VISIBILITY */}
            <div className={`${styles.field} ${styles.span4}`}>
              <label>Visibility</label>
              <select
                value={custom.visibility}
                onChange={e =>
                  onChange(prev => ({
                    custom: { ...prev.custom, visibility: e.target.value },
                  }))
                }
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>

            {/* WAGER */}
            <div className={`${styles.field} ${styles.span4}`}>
              <label>Wager (RatioCoin)</label>
              <input
                type="number"
                min="0"
                value={parseFloat(custom.wager).toFixed(2) || '0.00'}
                onChange={e =>
                  onChange(prev => ({
                    custom: { ...prev.custom, wager: +e.target.value },
                  }))
                }
              />
            </div>

            {/* HIGHLIGHT */}
            <div className={`${styles.highlightBox} ${styles.span12}`}>
              <label>Estimated Total Duration</label>
              <div>
                {totalTurnDurationMin}m {totalTurnDurationRemSec}s
                <span> ({totalTurnDurationSec}s)</span>
              </div>
            </div>

          </div>
        </section>

        {/* SCHEDULE */}
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Schedule Debate</h2>
            <p>Pick the date and time for the debate.</p>
          </div>

          <div className={styles.grid}>

            <div className={`${styles.field} ${styles.span6}`}>
              <label>Date</label>
              <input
                type="date"
                value={schedule?.date}
                onChange={e =>
                  onChange(prev => ({
                    schedule: { ...prev.schedule, date: e.target.value },
                  }))
                }
              />
            </div>

            <div className={`${styles.field} ${styles.span6}`}>
              <label>Time</label>
              <input
                type="time"
                value={schedule?.time}
                onChange={e =>
                  onChange(prev => ({
                    schedule: { ...prev.schedule, time: e.target.value },
                  }))
                }
              />
            </div>

          </div>

          {errors.schedule && <p className={styles.error}>{errors.schedule}</p>}
        </section>

        {/* ROLES */}
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Assign Roles</h2>
            <p>Select debaters and moderator.</p>
          </div>

          {errors.debaters && <p className={styles.error}>{errors.debaters}</p>}
          {errors.moderator && <p className={styles.error}>{errors.moderator}</p>}

          <DebaterAssign
          custom={custom}                     // team and participant config
          roles={roles}                       // current debater UID assignments
          currentUser={currentUser}           // current logged-in user object
          cache={cachedResults}
          resetSlots={custom.initialized}
          setCache={setCachedResults}
          handleUserSelect={handleUserSelect} // selection logic for adding user
          setSearchTarget={setSearchTarget}   // optional UI logic for modal/selector
          setSearchTargetTeam={setSearchTargetTeam}/>

          {isModeratorValid && (
            <div className={styles.teamSection}>
              <UserSelectField
              label={roleLabel}
              selected={roles.moderator}
              cache={cachedResults}
              setCache={setCachedResults}
              currentUser={currentUser}
              handleUserSelect={handleUserSelect}
              setSearchTarget={setSearchTarget}
              onPickUser={() => setSearchTarget('mod')}
              isCurrentUser={roles.moderator?.uid === currentUser.uid}
              onSelect={user => handleUserSelect(true, user, 0, undefined)}/>
            </div>
          )}
        </section>
      </>
    )}

    {/* BUTTONS */}
    <div className={styles.btnRow}>
      <Button variant="secondary" onClick={onBack}>Back</Button>
      <Button variant="primary" onClick={handleFinish}>Create Debate</Button>
    </div>

  </div>
);

return (
    <div className={styles.step}>
      <section>
        <h2>Select Format</h2>
        {errors?.format && <p className={styles.error}>{errors.format}</p>}
        <DebateFormatCarousel onSelect={id =>
          onChange(prev => ({
            formatId: id,
            custom: { ...prev.custom, initialized: false },
          }))
        } />
        <div className={styles.faqSection}>{faqs(formatFaqs)}</div>
      </section>

      {format && (
        <>
          <section>
            <h2>Customize Format</h2>
            <div className={styles.fieldGroup}>
              {['participants'].map(field => (
                <div key={field} className={styles.field}>
                  <label>{field === 'time' ? 'Seconds per turn' : field.charAt(0).toUpperCase() + field.slice(1)}</label>
                  <input
                    type="number"
                    min={format[field].min}
                    max={format[field]?.max || ''}
                    value={custom[field] || ''}
                    onChange={e =>
                      onChange(prev => ({
                        custom: { ...prev.custom, [field]: +e.target.value },
                      }))
                    }
                  />
                  {errors[field] && <p className={styles.error}>{errors[field]}</p>}
                </div>
              ))}
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <label>Estimated total duration</label>
                <div className={styles.readonlyValue}>
                  {totalTurnDurationMin}m {totalTurnDurationRemSec}s&nbsp; ({totalTurnDurationSec} seconds total)
                </div>
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <label>Visibility</label>
                <select value={custom.visibility} onChange={e =>
                  onChange(prev => ({ custom: { ...prev.custom, visibility: e.target.value } }))}>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
              <div className={styles.field}>
                <label>Wager (RatioCoin)</label>
                <input
                  type="number"
                  min="0"
                  value={parseFloat(custom.wager).toFixed(2) || parseFloat(0).toFixed(2)}
                  onChange={e =>
                    onChange(prev => ({ custom: { ...prev.custom, wager: +e.target.value } }))
                  }
                />
              </div>
            </div>
          </section>

          <section>
            <h2>Schedule Debate</h2>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <label>Date</label>
                <input
                  type="date"
                  value={schedule?.date}
                  onChange={e => onChange(prev => ({ schedule: { ...prev.schedule, date: e.target.value } }))}
                />
              </div>
              <div className={styles.field}>
                <label>Time</label>
                <input
                  type="time"
                  value={schedule?.time}
                  onChange={e => onChange(prev => ({ schedule: { ...prev.schedule, time: e.target.value } }))}
                />
              </div>
            </div>
            {errors.schedule && <p className={styles.error}>{errors.schedule}</p>}
          </section>

          {/*<section>
            <h2>Your Role</h2>
            <div className={styles.roleChoice}>
              <label><input type="radio" value="none" checked={myRole === 'none'} onChange={() => setMyRole('none')} /> None</label>
              <label><input type="radio" value="participant" checked={myRole === 'participant'} onChange={() => setMyRole('participant')} /> Debater</label>
              {format.rules.moderator && (
                <label><input type="radio" value="moderator" checked={myRole === 'moderator'} onChange={() => setMyRole('moderator')} /> Moderator</label>
              )}
            </div>
          </section>*/}

          <section>
            <h2>Assign Roles</h2>
            {errors.debaters && <p className={styles.error}>{errors.debaters}</p>}
            {errors.moderator && <p className={styles.error}>{errors.moderator}</p>}
			
			<DebaterAssign
			  custom={custom}                     // team and participant config
			  roles={roles}                       // current debater UID assignments
			  currentUser={currentUser}           // current logged-in user object
			  cache={cachedResults}
			  resetSlots={custom.initialized}
              setCache={setCachedResults}
			  handleUserSelect={handleUserSelect} // selection logic for adding user
			  setSearchTarget={setSearchTarget}   // optional UI logic for modal/selector
			  setSearchTargetTeam={setSearchTargetTeam}
			/>

			<div className={styles.teamSection}>
				{isModeratorValid && (
				  <UserSelectField
					label="Moderator"
					selected={roles.moderator}
					cache={cachedResults}
					setCache={setCachedResults}
					currentUser={currentUser}
					handleUserSelect={handleUserSelect}
					setSearchTarget={setSearchTarget}
					onPickUser={() => setSearchTarget('mod')}
					isCurrentUser={roles.moderator?.uid === currentUser.uid}
					onSelect={user => handleUserSelect(true, user, 0, undefined)}
				  />
				)}
			</div>
            {false && searchTarget !== null && (
              <UserSearchGeneric
                cache={cachedResults}
                setCache={setCachedResults}
				currentUser={currentUser}           // current logged-in user object
                onSelect={user => handleUserSelect(undefined, user, undefined, undefined)}
                onCancel={() => setSearchTarget(null)}
              />
            )}
          </section>
        </>
      )}

      <div className={styles.btnRow}>
        <Button variant="secondary" className={styles.backBtn} onClick={onBack}>Back</Button>
        <Button variant="primary" className={styles.finishBtn} onClick={handleFinish}>Create Debate</Button>
      </div>
    </div>
  );
}

export  function Step2FormatAndRolesdd({ data, onChange, onBack, onFinish }) {
  const { formatId, custom, roles } = data;
  const [myRole, setMyRole] = useState('none'); // 'participant', 'moderator', 'none'
  const [errors, setErrors] = useState({});
  const [searchTarget, setSearchTarget] = useState(null);
  const [cachedResults, setCachedResults] = useState({});
  const format = debateTypes.find(f => f.id === formatId);
  const {user, userData: currentUser, searchUsers} = useAuth();
  
	useEffect(() => {
	  if (!currentUser || !format) return;

	  const userEntry = {
		uid: currentUser.uid,
		username: currentUser.displayName || currentUser.email,
	  };

	  if (myRole === 'participant') {
		const arr = [...(roles.debaters || [])];
		const alreadyExists = arr.some(p => p?.uid === currentUser.uid);
		if (!alreadyExists) arr[0] = userEntry;
		onChange(prev => ({ roles: { ...prev.roles, debaters: arr } }));
	  }

	  if (myRole === 'moderator') {
		if (!roles.moderator || roles.moderator?.uid !== currentUser.uid) {
		  onChange(prev => ({ roles: { ...prev.roles, moderator: userEntry } }));
		}
	  }

	  if (myRole === 'none') {
		const arr = (roles.debaters || []).filter(p => p?.uid !== currentUser.uid);
		const mod = roles.moderator?.uid === currentUser.uid ? null : roles.moderator;
		onChange(prev => ({ roles: { debaters: arr, moderator: mod } }));
	  }
	}, [myRole]);

  const handleUserSelect = (user) => {
    if (searchTarget === 'mod') {
      onChange(prev => ({ roles: { ...prev.roles, moderator: user } }));
    } else {
      const arr = [...roles.debaters];
      arr[searchTarget] = user;
      onChange(prev => ({ roles: { ...prev.roles, debaters: arr } }));
    }
    setSearchTarget(null);
  };
  
  // Initialize defaults when format changes
  useEffect(() => {
    if (formatId && !custom.initialized) {
      onChange(prev => ({
        custom: {
          ...prev.custom,
          rounds: format.rules?.rounds?.default || 1,
          time: format.rules?.time?.default || 60,
          participants: format.rules.participants?.min || 1,
          visibility: prev.custom.visibility || 'public',
          wager: prev.custom.wager || 0,
          initialized: true,
        },
      }));
    }
  }, [formatId]);

  const validate = () => {
    const errs = {};
    if (!formatId) errs.format = 'Please select a format.';
    if (custom.participants < format.rules.participants.min || custom.participants > (format.rules.participants.max || Infinity)) {
      errs.participants = `Participants must be between ${format.rules.participants.min}${format.rules.participants.max ? ' and ' + format.rules.participants.max : '+'}.`;
    }
    if (custom.rounds < (format.rules?.rounds?.min || 1)) {
      errs.rounds = `At least ${format.rules?.rounds?.min || 1} rounds required.`;
    }
    if (custom.time < (format.rules?.time?.min || 1) || custom.time > (format.rules?.time?.max || Infinity)) {
      errs.time = `Time must be between ${format.rules?.time?.min || 1}-${format.rules?.time?.max || Infinity}s.`;
    }
    if (format.rules.moderator && !roles.moderator) {
      errs.moderator = 'A moderator is required for this format.';
    }
    if (!roles.debaters || roles.debaters.length < custom.participants) {
      errs.debaters = `Add ${custom.participants} debaters.`;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFinish = () => {
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    onFinish();
  };

  return (
    <div className={styles.step}>
      {/* Format Selector */}
      <section>
        <h2>Select Format</h2>
        {errors.format && <p className={styles.error}>{errors.format}</p>}
        <DebateFormatCarousel onSelect={id => onChange(prev => ({
          formatId: id,
          custom: { ...prev.custom, initialized: false },
          roles: { debaters: [], moderator: null },
        }))} />
        <FAQItem topic={formatId} />
      </section>

      {format && (
        <>
		{/* Customize user */}
		<section>
		  <h2>My Role</h2>
		  <div className={styles.roleChoice}>
			<label>
			  <input
				type="radio"
				name="myRole"
				value="none"
				checked={myRole === 'none'}
				onChange={() => setMyRole('none')}
			  />
			  None
			</label>
			<label>
			  <input
				type="radio"
				name="myRole"
				value="participant"
				checked={myRole === 'participant'}
				onChange={() => setMyRole('participant')}
			  />
			  Join as Debater
			</label>
			{format?.rules?.moderator && (
			  <label>
				<input
				  type="radio"
				  name="myRole"
				  value="moderator"
				  checked={myRole === 'moderator'}
				  onChange={() => setMyRole('moderator')}
				/>
				Join as Moderator
			  </label>
			)}
		  </div>
		</section>
		  {/* Customize Format */}
          <section>
            <h2>Customize Format</h2>
            <div className={styles.fieldGroup}>
            {['rounds','participants','time'].map(field => (
              <div key={field} className={styles.field}>
                <label>
                  {field.charAt(0).toUpperCase() + field.slice(1)} ({field==='time'?'time/turn (s)':field})
                </label>
                <input
                  type="number"
                  value={custom[field] || ''}
                  min={format.rules[field]?.min || 1}
                  max={format.rules[field]?.max || ''}
                  onChange={e => onChange(prev => ({
                    custom: { ...prev.custom, [field]: parseInt(e.target.value, 10) }
                  }))}
                />
                {errors[field] && <p className={styles.error}>{errors[field]}</p>}
              </div>
            ))}
          </div>

          {/* Visibility and Wager */}
          <div className={styles.fieldGroup}>
            <div className={styles.field}>
              <label>Visibility</label>
              <select value={custom.visibility} onChange={e =>
                onChange(prev => ({ custom: { ...prev.custom, visibility: e.target.value } }))}>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>

            <div className={styles.field}>
              <label>Wager (RatioCoin)</label>
              <input
                type="number"
                min="0"
                value={custom.wager || 0}
                onChange={e => onChange(prev => ({
                  custom: { ...prev.custom, wager: parseInt(e.target.value,10) },
                }))}
              />
            </div>
          </div>
        </section>

		   {/* Roles */}
          {/* Roles Assignment */}
          <section>
            <h2>Assign Roles</h2>
            <FAQItem topic="assignRoles" />
            {errors.debaters && <p className={styles.error}>{errors.debaters}</p>}
            {errors.moderator && <p className={styles.error}>{errors.moderator}</p>}

            {[...Array(custom.participants)].map((_, i) => (
              <UserSelectField
                key={i}
                label={`Debater #${i + 1}`}
                selected={roles.debaters[i]}
                onPickUser={() => setSearchTarget(i)}
                onSelect={user => {
                  const arr = [...roles.debaters];
                  arr[i] = user;
                  onChange(prev => ({ roles: { ...prev.roles, debaters: arr } }));
                }}
              />
            ))}

            {format.rules.moderator && (
              <UserSelectField
                label="Moderator"
                selected={roles.moderator}
                onPickUser={() => setSearchTarget('mod')}
                onSelect={m => onChange(prev => ({ roles: { ...prev.roles, moderator: m } }))}
              />
            )}

            {/* Inline UserSearch */}
            {searchTarget !== null && (
              <UserSearchGeneric
                cache={cachedResults}
                setCache={setCachedResults}
                onSelect={handleUserSelect}
                onCancel={() => setSearchTarget(null)}
              />
            )}
          </section>
		</>)}

      <div className={styles.btnRow}>
        <Button variant="secondary" className={styles.backBtn} onClick={onBack}>Back</Button>
        <Button variant="primary" className={styles.finishBtn} onClick={handleFinish}>Create Debate</Button>
      </div>
    </div>
  );
}
